import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, onMounted, ref, watch } from "vue";

import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { IPaymentMethod, IServicePaymentMethods } from "@/data/model/Customer";

import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";

import { operatorEvents } from "@/operator/events/operatorEvents";
import { PayContext } from "@/operator/movedComponents/PayContext";

import { useBillStore } from "./bill";
import { useCartStore } from "./cart";
import { useOfferStore } from "./offer";
import { useOrderedArticlesStore } from "./orderedArticles";

export const useCartPaymentStore = defineStore("cartPayment", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const payContext = new PayContext(0);

    const cart = useCartStore();
    const offer = useOfferStore();
    const bill = useBillStore();
    const orderedArticles = useOrderedArticlesStore();

    // state
    const paymentService = computed(() => offer.orderService);
    const isProcessingPayment = ref(false);
    const isLoadingPaymentMethods = ref(false);
    const availablePaymentMethods = ref<IServicePaymentMethods | null>(null);
    const isProcessingPaymentDevicePayment = ref(false);
    const tip = ref(0);

    // getters
    const operatorPaymentMethods = computed(() => availablePaymentMethods.value?.paymentMethods ?? []);
    const operatorQuickPaymentMethods = computed(() => availablePaymentMethods.value?.quickPaymentMethods ?? []);
    const ordererRequirements = computed(() => offer.orderService?.useCase?.ordererRequirements);
    const isEmailRequired = computed(() => ordererRequirements.value?.isEmailRequired ?? false);
    const enabledFieldRequirementsCount = computed(() => ordererRequirements.value?.enabledFieldRequirementsCount ?? 0);
    const isRegisteredRequired = computed<boolean>(() => ordererRequirements.value?.isRegisteredRequired === true);
    const contactDataEmail = computed<string | undefined>(() => cart.currentCart?.contactDataEndUser?.email);

    // actions
    async function payImmediatelly(
        consumption: number,
        totalDiscount: number,
        tip: number,
        paymentMethod: IPaymentMethod
    ): Promise<boolean> {
        try {
            isProcessingPayment.value = true;
            isProcessingPaymentDevicePayment.value = paymentMethod.isPaymentDevice;

            if (!paymentService.value) throw translations.pay.noActivePaymentService;

            payContext.setPaymentMethod(paymentMethod);

            // todo: verify order service passing as param - maybe only id is enough
            const onGoBackToFulfillmentTimeView = () => {
                operatorEvents.payContext.goBackToPreorderTimeSelectionRequested.dispatch();
            };
            await payContext.pay({
                selectedService: paymentService.value,
                cartHash: cart.currentCart!.hash,
                consumption,
                totalDiscount,
                tip,
                force: true,
                selectedItems: undefined,
                onGoBackToFulfillmentTimeView: onGoBackToFulfillmentTimeView,
            });
            return true;
        } catch {
            return false;
        } finally {
            isProcessingPayment.value = false;
            isProcessingPaymentDevicePayment.value = false;
        }
    }

    async function loadAvailablePaymentMethods() {
        try {
            isLoadingPaymentMethods.value = true;
            availablePaymentMethods.value = null;

            if (!paymentService.value) {
                return;
            }

            availablePaymentMethods.value = await customerDao.getServicePaymentMethods(paymentService.value.id);
        } catch (ex) {
            logError(ex);
        } finally {
            isLoadingPaymentMethods.value = false;
        }
    }

    watch(paymentService, () => {
        loadAvailablePaymentMethods();
    });

    onMounted(() => {
        loadAvailablePaymentMethods();
    });

    payContext.onCheckoutPayLaterCartSuccess.add(async () => await onCheckoutCartSuccess());
    payContext.onCheckoutPayImmediatellyCartSuccess.add(async () => await onCheckoutCartSuccess());

    async function onCheckoutCartSuccess() {
        console.log("checkout cart success");

        // todo: ts-1064 - this is a fallback - we should get websocket notification, but this should work just in case
        await bill.loadCurrentBill();
        orderedArticles.refresh().catch(logError);

        cart.resetCart();
    }

    return {
        // state
        paymentService,
        isProcessingPayment,
        isLoadingPaymentMethods,
        isProcessingPaymentDevicePayment,
        tip,

        // getters
        operatorPaymentMethods,
        operatorQuickPaymentMethods,
        isEmailRequired,
        contactDataEmail,
        enabledFieldRequirementsCount,
        isRegisteredRequired,

        // actions
        payImmediatelly,
    };
});

export type CartPaymentStore = ReturnType<typeof useCartPaymentStore>;

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCartPaymentStore, import.meta.hot));
}
